import React from "react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Plus, Copy, Trash2, FileText, Loader2 } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

interface AdSheet {
  id: string;
  name: string;
  ad_platform: string;
  created_at: string;
  updated_at: string;
  ad_account_id: string;
  user_id: string;
}

export default function AdSheetsDirectory() {
  const { user } = useAuth();
  const { selectedAdAccountId, selectedAdAccountName } = useAdAccount();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [sheets, setSheets] = useState<AdSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewSheetModal, setShowNewSheetModal] = useState(false);
  const [deleteSheetId, setDeleteSheetId] = useState<string | null>(null);
  const [editingSheetId, setEditingSheetId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [duplicating, setDuplicating] = useState<string | null>(null);

  // New sheet form state
  const [newSheetName, setNewSheetName] = useState("Untitled Sheet");
  const [newSheetPlatform, setNewSheetPlatform] = useState("Meta Ads");
  const [creating, setCreating] = useState(false);

  const fetchSheets = useCallback(async () => {
    if (!user?.id || !selectedAdAccountId) {
      setSheets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("ad_sheets")
        .select("id, name, ad_platform, created_at, updated_at, ad_account_id, user_id")
        .eq("user_id", user.id)
        .eq("ad_account_id", selectedAdAccountId)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setSheets(data || []);
    } catch (error) {
      console.error("Error fetching sheets:", error);
      toast({
        title: "Error",
        description: "Failed to load ad sheets",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedAdAccountId, toast]);

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  const handleCreateSheet = async () => {
    if (!user?.id || !selectedAdAccountId) return;

    setCreating(true);
    try {
      const { data, error } = await supabase
        .from("ad_sheets")
        .insert({
          name: newSheetName.trim() || "Untitled Sheet",
          ad_platform: newSheetPlatform,
          user_id: user.id,
          ad_account_id: selectedAdAccountId,
        })
        .select()
        .single();

      if (error) throw error;

      toast({ title: "Sheet created", description: "Your new ad sheet is ready." });
      setShowNewSheetModal(false);
      setNewSheetName("Untitled Sheet");
      setNewSheetPlatform("Meta Ads");
      
      // Navigate to the new sheet
      navigate(`/launcher/ad-sheets/${data.id}`);
    } catch (error) {
      console.error("Error creating sheet:", error);
      toast({
        title: "Error",
        description: "Failed to create ad sheet",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicateSheet = async (sheetId: string) => {
    if (!user?.id) return;

    setDuplicating(sheetId);
    try {
      // Get the original sheet
      const originalSheet = sheets.find((s) => s.id === sheetId);
      if (!originalSheet) return;

      // Create the duplicated sheet
      const { data: newSheet, error: sheetError } = await supabase
        .from("ad_sheets")
        .insert({
          name: `${originalSheet.name} (Copy)`,
          ad_platform: originalSheet.ad_platform,
          user_id: user.id,
          ad_account_id: originalSheet.ad_account_id,
        })
        .select()
        .single();

      if (sheetError) throw sheetError;

      // Get all rows from the original sheet
      const { data: originalRows, error: rowsError } = await supabase
        .from("ad_sheet_rows")
        .select("*")
        .eq("sheet_id", sheetId);

      if (rowsError) throw rowsError;

      // Duplicate the rows if any exist
      if (originalRows && originalRows.length > 0) {
        const newRows = originalRows.map((row) => ({
          ...row,
          id: undefined, // Let Supabase generate new IDs
          sheet_id: newSheet.id,
          user_id: user.id,
          created_at: undefined,
          updated_at: undefined,
          meta_ad_id: null,
          meta_creative_id: null,
          launched_at: null,
          launch_error: null,
          stage: "Draft",
        }));

        const { error: insertError } = await supabase
          .from("ad_sheet_rows")
          .insert(newRows);

        if (insertError) throw insertError;
      }

      toast({
        title: "Sheet duplicated",
        description: `Created "${newSheet.name}" with ${originalRows?.length || 0} rows.`,
      });
      fetchSheets();
    } catch (error) {
      console.error("Error duplicating sheet:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate ad sheet",
        variant: "destructive",
      });
    } finally {
      setDuplicating(null);
    }
  };

  const handleDeleteSheet = async () => {
    if (!deleteSheetId) return;

    try {
      // Delete all rows first
      const { error: rowsError } = await supabase
        .from("ad_sheet_rows")
        .delete()
        .eq("sheet_id", deleteSheetId);

      if (rowsError) throw rowsError;

      // Delete the sheet
      const { error: sheetError } = await supabase
        .from("ad_sheets")
        .delete()
        .eq("id", deleteSheetId);

      if (sheetError) throw sheetError;

      toast({ title: "Sheet deleted", description: "The ad sheet has been removed." });
      setDeleteSheetId(null);
      fetchSheets();
    } catch (error) {
      console.error("Error deleting sheet:", error);
      toast({
        title: "Error",
        description: "Failed to delete ad sheet",
        variant: "destructive",
      });
    }
  };

  const handleUpdateName = async (sheetId: string, newName: string) => {
    try {
      const { error } = await supabase
        .from("ad_sheets")
        .update({ name: newName.trim() || "Untitled Sheet" })
        .eq("id", sheetId);

      if (error) throw error;

      setSheets((prev) =>
        prev.map((s) => (s.id === sheetId ? { ...s, name: newName.trim() || "Untitled Sheet" } : s))
      );
      toast({ title: "Name updated" });
    } catch (error) {
      console.error("Error updating name:", error);
      toast({
        title: "Error",
        description: "Failed to update sheet name",
        variant: "destructive",
      });
    } finally {
      setEditingSheetId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, "dd.MM.yyyy");
  };

  if (!selectedAdAccountId) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Please select an ad account from the sidebar to view your ad sheets.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Ad Sheets</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {selectedAdAccountName || selectedAdAccountId}
          </p>
        </div>
        <Button onClick={() => setShowNewSheetModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Sheet
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : sheets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <FileText className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h2 className="text-lg font-medium text-foreground mb-2">No ad sheets yet</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first sheet to get started.
            </p>
            <Button onClick={() => setShowNewSheetModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Sheet
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Name</TableHead>
                <TableHead className="w-[120px]">Ad Platform</TableHead>
                <TableHead className="w-[150px]">Last Modified</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sheets.map((sheet) => (
                <TableRow key={sheet.id} className="group">
                  <TableCell>
                    {editingSheetId === sheet.id ? (
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => handleUpdateName(sheet.id, editingName)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleUpdateName(sheet.id, editingName);
                          } else if (e.key === "Escape") {
                            setEditingSheetId(null);
                          }
                        }}
                        autoFocus
                        className="h-8"
                      />
                    ) : (
                      <button
                        onClick={() => navigate(`/launcher/ad-sheets/${sheet.id}`)}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setEditingSheetId(sheet.id);
                          setEditingName(sheet.name);
                        }}
                        className="text-left font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {sheet.name}
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{sheet.ad_platform}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(sheet.updated_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDuplicateSheet(sheet.id)}
                        disabled={duplicating === sheet.id}
                      >
                        {duplicating === sheet.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteSheetId(sheet.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* New Sheet Modal */}
      <Dialog open={showNewSheetModal} onOpenChange={setShowNewSheetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Ad Sheet</DialogTitle>
            <DialogDescription>
              Create a new sheet to organize your bulk ad creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sheet-name">Name</Label>
              <Input
                id="sheet-name"
                value={newSheetName}
                onChange={(e) => setNewSheetName(e.target.value)}
                placeholder="Untitled Sheet"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="platform">Ad Platform</Label>
              <Select value={newSheetPlatform} onValueChange={setNewSheetPlatform}>
                <SelectTrigger id="platform">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Meta Ads">Meta Ads</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSheetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSheet} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteSheetId} onOpenChange={() => setDeleteSheetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ad Sheet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this ad sheet and all its rows. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSheet}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
